/**
 * Platform Reinitializer
 *
 * Handles the registration of all page-specific CDN functions for SPA navigation.
 *
 * Problem: CDN scripts (GA4, GTM) define page-specific functions conditionally
 * based on `shopify_template` at init time. In SPA, scripts only load once,
 * so navigating to a different page type means those functions aren't defined.
 *
 * Solution: At SDK init, we loop through all page types (with tracking suppressed)
 * to force the CDN to define all functions. Then on navigation, we just call
 * the already-defined functions directly.
 */

import type { WindowAnalyzify, PlatformProperties } from '../types/window-analyzify';
import { runWithDedupedListeners } from './listener-dedupe';

/** Page types that have conditional function definitions */
const PAGE_TYPES_WITH_CONDITIONAL_FUNCTIONS = ['product', 'collection', 'cart', 'search'] as const;

type PageType = (typeof PAGE_TYPES_WITH_CONDITIONAL_FUNCTIONS)[number];

/**
 * Saved state during function registration
 * Includes ALL tracking APIs that need suppression for SPA compatibility
 */
interface SavedState {
  // GA4/GTM APIs
  gtag: ((...args: any[]) => void) | undefined;
  dataLayerPush: ((...args: any[]) => number) | undefined;

  // Bing UET API - CRITICAL: This was causing the bingViewItemList error
  uetqPush: ((...args: any[]) => number) | undefined;

  // Facebook Pixel API
  fbq: ((...args: any[]) => void) | undefined;

  // TikTok Pixel API
  ttq: any;

  // Pinterest Tag API
  pintrk: ((...args: any[]) => void) | undefined;

  // Snapchat Pixel API
  snaptr: ((...args: any[]) => void) | undefined;

  // Criteo OneTag API
  criteoQPush: ((...args: any[]) => number) | undefined;

  // X/Twitter Pixel API
  twq: ((...args: any[]) => void) | undefined;

  // Microsoft Clarity API
  clarity: ((...args: any[]) => void) | undefined;

  // Hotjar API
  hj: ((...args: any[]) => void) | undefined;

  // Klaviyo API
  klaviyo: any;
  _learnqPush: ((...args: any[]) => number) | undefined;

  // Page state
  shopify_template: string;
  getProductObj: any;
  getCollectionObj: any;
  collection: any;
  detectedCart: any;
  getSearchObj: any;
  ga4Initiated: boolean;
  gtmInitiated: boolean;
  ga4InitializeAndTrackPage: (() => void) | undefined;
  gtmInitializeAndTrackPage: (() => void) | undefined;
}

/**
 * PlatformReinitializer class
 *
 * Ensures all page-specific CDN functions are defined regardless of initial page type.
 */
export class PlatformReinitializer {
  private analyzify: WindowAnalyzify;
  private properties: PlatformProperties;
  private functionsRegistered = false;

  constructor(analyzify: WindowAnalyzify) {
    this.analyzify = analyzify;
    this.properties = analyzify.properties;
  }

  /**
   * Register all page-specific functions from GA4 and GTM CDN scripts.
   *
   * Called once after initial platform initialization.
   * Loops through all page types with tracking suppressed to ensure
   * all functions are defined and available for SPA navigation.
   */
  async registerAllFunctions(): Promise<void> {
    if (this.functionsRegistered) {
      this.analyzify.log('Functions already registered, skipping', 'platform-reinit');
      return;
    }

    const ga4Enabled = this.properties.GA4?.status || this.properties.GADS?.status;
    const gtmEnabled = this.properties.GTM?.status;
    const klaviyoEnabled = this.properties.KLAVIYO?.status;

    // If no platforms that need function registration are enabled, nothing to do
    // Note: Klaviyo needs special handling during 'collection' page type, so we include it
    if (!ga4Enabled && !gtmEnabled && !klaviyoEnabled) {
      this.analyzify.log('GA4, GTM, and Klaviyo not enabled, skipping function registration', 'platform-reinit');
      this.functionsRegistered = true;
      return;
    }

    this.analyzify.log('Starting function registration for all page types', 'platform-reinit');

    // CRITICAL: Ensure collection object has products array BEFORE any CDN init calls
    // CDN's an_analyzify.js creates analyzify.collection = { id, title, handle } WITHOUT products.
    // CDN scripts like GTM/Bing check shopify_template === 'collection' and try to read
    // collection.products.map() - this fails if products is undefined.
    // We MUST ensure products array exists, not just check if collection is truthy.
    const analyzify = this.analyzify as any;
    const win = window as any;
    const defaultCollection = {
      id: analyzify.collection?.id ?? 0,
      title: analyzify.collection?.title ?? '',
      handle: analyzify.collection?.handle ?? '',
      products: [],
      all_products_count: 0,
      products_count: 0,
    };
    if (!analyzify.collection || !Array.isArray(analyzify.collection.products)) {
      analyzify.collection = defaultCollection;
    }
    // CRITICAL: Also set window.collection for CDN scripts that use bare variable references
    // The script-loader.ts creates `var collection = window.collection || null;` but the
    // bare variable might already be captured as null. Setting window.collection helps
    // CDN scripts that read from window.collection (vs bare variable) during init.
    if (!win.collection || !Array.isArray(win.collection.products)) {
      win.collection = analyzify.collection;
    }

    // 1. Save current state
    const savedState = this.saveState();

    // 2. Suppress all tracking APIs
    this.suppressTrackingApis();

    // 3. Loop through each page type to register all functions.
    //    Each pass re-adds the CDN's document/window listeners (form lead
    //    tracking etc.); every pass replaces the previous pass's copies.
    for (const pageType of PAGE_TYPES_WITH_CONDITIONAL_FUNCTIONS) {
    runWithDedupedListeners(() => {
      // Set template type
      this.analyzify.shopify_template = pageType;

      // Provide mock data for this page type
      this.provideMockData(pageType);

      // Reset init flags so CDN thinks it hasn't run yet
      (this.analyzify as any).ga4Initiated = false;
      (this.analyzify as any).gtmInitiated = false;

      // Re-call platform init functions
      // CDN will define page-specific functions and auto-call them (but calls hit no-ops)
      if (ga4Enabled && typeof this.analyzify.initGa4Gads === 'function') {
        try {
          this.analyzify.initGa4Gads(
            this.analyzify,
            this.properties.GA4,
            this.properties.GADS
          );
          this.analyzify.log(`GA4 functions registered for ${pageType}`, 'platform-reinit');
        } catch (error) {
          this.analyzify.log(`Error registering GA4 functions for ${pageType}: ${error}`, 'platform-reinit');
        }
      }

      if (gtmEnabled && typeof this.analyzify.initGTM === 'function') {
        try {
          this.analyzify.initGTM(this.analyzify, this.properties.GTM);
          this.analyzify.log(`GTM functions registered for ${pageType}`, 'platform-reinit');
        } catch (error) {
          this.analyzify.log(`Error registering GTM functions for ${pageType}: ${error}`, 'platform-reinit');
        }
      }

      // CRITICAL: Also call Klaviyo init during 'collection' page type
      // Klaviyo CDN captures bare variables (collection, taxonomy, product, variant, cart)
      // in closures only when shopify_template === 'collection'.
      // Without this, klaviyoAddToCart will throw ReferenceError when called from non-collection pages.
      // We also set a flag to prevent attemptInitialization() from re-calling initKlaviyo()
      // which would overwrite the function and lose the captured variables.
      if (pageType === 'collection' && this.properties.KLAVIYO?.status && typeof this.analyzify.initKlaviyo === 'function') {
        try {
          // CRITICAL: Set global variables that Klaviyo CDN expects
          // These are bare variable references (not window.x) that CDN captures in closures
          const win = window as any;
          const analyzify = this.analyzify as any;

          // Set window-level globals that CDN script reads
          win.collection = analyzify.collection || { id: 0, title: '', handle: '', products: [] };
          win.taxonomy = analyzify.taxonomy || { category: '', breadcrumbs: [] };
          win.product = analyzify.product || null;
          win.variant = analyzify.getProductObj?.variant || null;
          win.cart = analyzify.detectedCart || { items: [], total_price: 0 };

          this.analyzify.initKlaviyo(this.analyzify, this.properties.KLAVIYO);
          (this.analyzify as any).__klaviyo_registered_with_collection__ = true;
          this.analyzify.log('Klaviyo collection functions registered', 'platform-reinit');
        } catch (error) {
          this.analyzify.log(`Error registering Klaviyo functions: ${error}`, 'platform-reinit');
        }
      }
    });
    }

    // 4. Restore original state
    this.restoreState(savedState);

    this.functionsRegistered = true;
    this.analyzify.log('All page-specific functions registered successfully', 'platform-reinit');

    // Log which functions are now available
    this.logRegisteredFunctions();
  }

  /**
   * Save current state before function registration
   *
   * CRITICAL FOR SPA: We must save ALL tracking API references so we can
   * restore them after registration. During registration, CDN scripts will
   * attempt auto-tracking which would fire with mock/incomplete data.
   */
  private saveState(): SavedState {
    const win = window as any;
    return {
      // GA4/GTM APIs
      gtag: win.gtag,
      dataLayerPush: win.dataLayer?.push,

      // Bing UET API - CRITICAL: uetq.push() is used for all Bing events
      uetqPush: win.uetq?.push,

      // Facebook Pixel API
      fbq: win.fbq,

      // TikTok Pixel API - Save entire object to preserve instance method
      ttq: win.ttq
        ? {
            track: win.ttq.track,
            page: win.ttq.page,
            identify: win.ttq.identify,
            instance: win.ttq.instance,
          }
        : undefined,

      // Pinterest Tag API
      pintrk: win.pintrk,

      // Snapchat Pixel API
      snaptr: win.snaptr,

      // Criteo OneTag API
      criteoQPush: win.criteo_q?.push,

      // X/Twitter Pixel API
      twq: win.twq,

      // Microsoft Clarity API
      clarity: win.clarity,

      // Hotjar API
      hj: win.hj,

      // Klaviyo API - Save methods that trigger tracking
      klaviyo: win.klaviyo
        ? {
            track: win.klaviyo.track,
            identify: win.klaviyo.identify,
            push: win.klaviyo.push,
          }
        : undefined,
      _learnqPush: win._learnq?.push,

      // Page state
      shopify_template: this.analyzify.shopify_template,
      getProductObj: (this.analyzify as any).getProductObj,
      getCollectionObj: (this.analyzify as any).getCollectionObj,
      collection: (this.analyzify as any).collection,
      detectedCart: this.analyzify.detectedCart,
      getSearchObj: (this.analyzify as any).getSearchObj,
      ga4Initiated: (this.analyzify as any).ga4Initiated || false,
      gtmInitiated: (this.analyzify as any).gtmInitiated || false,
      ga4InitializeAndTrackPage: (this.analyzify as any).ga4InitializeAndTrackPage,
      gtmInitializeAndTrackPage: (this.analyzify as any).gtmInitializeAndTrackPage,
    };
  }

  /**
   * Suppress ALL tracking APIs during function registration
   *
   * CRITICAL FOR SPA COMPATIBILITY:
   * CDN scripts were designed for traditional Shopify where all data exists at page load.
   * When CDN scripts initialize, they check shopify_template and auto-track page views.
   *
   * In SPA, we call platform init functions during registerAllFunctions() to pre-define
   * all tracking functions (gaViewItem, bingViewItemList, etc.) for later use.
   * But during this registration, CDN scripts will attempt auto-tracking with mock data.
   *
   * We MUST suppress ALL tracking APIs to prevent:
   * 1. Incorrect data being sent to platforms (mock/empty data)
   * 2. Errors from accessing undefined properties (e.g., collection.products.map())
   * 3. Duplicate events when real tracking happens later
   *
   * After registration completes, we restore all APIs to their original state.
   */
  private suppressTrackingApis(): void {
    const win = window as any;

    // ═══════════════════════════════════════════════════════════════════════════
    // GA4 / Google Ads - gtag()
    // ═══════════════════════════════════════════════════════════════════════════
    win.gtag = () => {
      // No-op during registration
    };

    // ═══════════════════════════════════════════════════════════════════════════
    // GTM - dataLayer.push()
    // ═══════════════════════════════════════════════════════════════════════════
    if (win.dataLayer && Array.isArray(win.dataLayer)) {
      win.dataLayer.push = () => win.dataLayer.length;
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // Bing UET - uetq.push()
    // CRITICAL: This was causing "Cannot read properties of undefined (reading 'map')"
    // because bingViewItemList auto-fires during initBing() and reads collection.products
    // ═══════════════════════════════════════════════════════════════════════════
    if (win.uetq && Array.isArray(win.uetq)) {
      win.uetq.push = () => win.uetq.length;
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // Facebook Pixel - fbq()
    // ═══════════════════════════════════════════════════════════════════════════
    if (typeof win.fbq === 'function') {
      win.fbq = () => {
        // No-op during registration
      };
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // TikTok Pixel - ttq.track(), ttq.page(), ttq.instance()
    // TikTok has multiple entry points that need suppression
    // ═══════════════════════════════════════════════════════════════════════════
    if (win.ttq) {
      win.ttq.track = () => {};
      win.ttq.page = () => {};
      win.ttq.identify = () => {};
      // ttq.instance() returns an object with track/page methods
      if (typeof win.ttq.instance === 'function') {
        const originalInstance = win.ttq.instance;
        win.ttq.instance = () => ({
          track: () => {},
          page: () => {},
          identify: () => {},
        });
      }
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // Pinterest Tag - pintrk()
    // ═══════════════════════════════════════════════════════════════════════════
    if (typeof win.pintrk === 'function') {
      win.pintrk = () => {
        // No-op during registration
      };
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // Snapchat Pixel - snaptr()
    // ═══════════════════════════════════════════════════════════════════════════
    if (typeof win.snaptr === 'function') {
      win.snaptr = () => {
        // No-op during registration
      };
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // Criteo OneTag - criteo_q.push()
    // ═══════════════════════════════════════════════════════════════════════════
    if (win.criteo_q && Array.isArray(win.criteo_q)) {
      win.criteo_q.push = () => win.criteo_q.length;
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // X (Twitter) Pixel - twq()
    // ═══════════════════════════════════════════════════════════════════════════
    if (typeof win.twq === 'function') {
      win.twq = () => {
        // No-op during registration
      };
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // Microsoft Clarity - clarity()
    // ═══════════════════════════════════════════════════════════════════════════
    if (typeof win.clarity === 'function') {
      win.clarity = () => {
        // No-op during registration
      };
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // Hotjar - hj()
    // ═══════════════════════════════════════════════════════════════════════════
    if (typeof win.hj === 'function') {
      win.hj = () => {
        // No-op during registration
      };
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // Klaviyo - klaviyo.track(), klaviyo.identify(), _learnq.push()
    // Klaviyo has multiple APIs for different purposes
    // ═══════════════════════════════════════════════════════════════════════════
    if (win.klaviyo) {
      if (typeof win.klaviyo.track === 'function') {
        win.klaviyo.track = () => {};
      }
      if (typeof win.klaviyo.identify === 'function') {
        win.klaviyo.identify = () => {};
      }
      if (typeof win.klaviyo.push === 'function') {
        win.klaviyo.push = () => {};
      }
    }

    // _learnq is Klaviyo's legacy queue API
    if (win._learnq && Array.isArray(win._learnq)) {
      win._learnq.push = () => win._learnq.length;
    }

    this.analyzify.log('All tracking APIs suppressed for function registration', 'platform-reinit');
  }

  /**
   * Provide mock data for a specific page type
   */
  private provideMockData(pageType: PageType): void {
    const analyzify = this.analyzify as any;

    switch (pageType) {
      case 'product':
        analyzify.getProductObj = {
          product: {
            id: 0,
            title: 'Mock Product',
            vendor: 'Mock',
            type: 'Mock',
            price: 0,
            variants: [{ id: 0, title: 'Default', price: 0, available: true }],
          },
          variant: { id: 0, title: 'Default', price: 0, available: true },
        };
        break;

      case 'collection':
        // CRITICAL: Set BOTH analyzify.collection AND getCollectionObj
        // CDN's initGTM/initBing read analyzify.collection.products directly
        // not getCollectionObj.collection.products
        const mockCollection = {
          id: 0,
          title: 'Mock Collection',
          handle: 'mock-collection',
          products: [],
          all_products_count: 0,
          products_count: 0,
        };
        analyzify.collection = mockCollection;
        analyzify.getCollectionObj = {
          products: [],
          id: 0,
          title: 'Mock Collection',
          handle: 'mock-collection',
          collection: mockCollection,
          collection_sku_list: [],
        };
        // Also set window.collection for bare variable references
        (window as any).collection = mockCollection;
        break;

      case 'cart':
        analyzify.detectedCart = {
          items: [],
          total_price: 0,
          item_count: 0,
          currency: this.analyzify.currency || 'USD',
        };
        break;

      case 'search':
        analyzify.getSearchObj = {
          term: '',
          products: [],
          resultsCount: 0,
          searchPerformed: false,
        };
        break;
    }
  }

  /**
   * Restore ALL tracking APIs after function registration
   *
   * CRITICAL FOR SPA: After registration completes, we must restore all
   * tracking APIs to their original state so actual tracking can work.
   */
  private restoreState(savedState: SavedState): void {
    const win = window as any;
    const analyzify = this.analyzify as any;

    // ═══════════════════════════════════════════════════════════════════════════
    // RESTORE ALL TRACKING APIs
    // ═══════════════════════════════════════════════════════════════════════════

    // GA4/GTM - gtag
    if (savedState.gtag) {
      win.gtag = savedState.gtag;
    }

    // GTM - dataLayer.push
    if (savedState.dataLayerPush && win.dataLayer) {
      win.dataLayer.push = savedState.dataLayerPush;
    }

    // Bing UET - uetq.push
    if (savedState.uetqPush && win.uetq) {
      win.uetq.push = savedState.uetqPush;
    }

    // Facebook Pixel - fbq
    if (savedState.fbq) {
      win.fbq = savedState.fbq;
    }

    // TikTok Pixel - ttq methods
    if (savedState.ttq && win.ttq) {
      if (savedState.ttq.track) win.ttq.track = savedState.ttq.track;
      if (savedState.ttq.page) win.ttq.page = savedState.ttq.page;
      if (savedState.ttq.identify) win.ttq.identify = savedState.ttq.identify;
      if (savedState.ttq.instance) win.ttq.instance = savedState.ttq.instance;
    }

    // Pinterest Tag - pintrk
    if (savedState.pintrk) {
      win.pintrk = savedState.pintrk;
    }

    // Snapchat Pixel - snaptr
    if (savedState.snaptr) {
      win.snaptr = savedState.snaptr;
    }

    // Criteo OneTag - criteo_q.push
    if (savedState.criteoQPush && win.criteo_q) {
      win.criteo_q.push = savedState.criteoQPush;
    }

    // X/Twitter Pixel - twq
    if (savedState.twq) {
      win.twq = savedState.twq;
    }

    // Microsoft Clarity - clarity
    if (savedState.clarity) {
      win.clarity = savedState.clarity;
    }

    // Hotjar - hj
    if (savedState.hj) {
      win.hj = savedState.hj;
    }

    // Klaviyo - klaviyo methods
    if (savedState.klaviyo && win.klaviyo) {
      if (savedState.klaviyo.track) win.klaviyo.track = savedState.klaviyo.track;
      if (savedState.klaviyo.identify) win.klaviyo.identify = savedState.klaviyo.identify;
      if (savedState.klaviyo.push) win.klaviyo.push = savedState.klaviyo.push;
    }

    // Klaviyo legacy - _learnq.push
    if (savedState._learnqPush && win._learnq) {
      win._learnq.push = savedState._learnqPush;
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // RESTORE PAGE STATE
    // ═══════════════════════════════════════════════════════════════════════════

    this.analyzify.shopify_template = savedState.shopify_template;
    analyzify.getProductObj = savedState.getProductObj;
    analyzify.getCollectionObj = savedState.getCollectionObj;
    // CRITICAL: Preserve collection.products array when restoring
    // savedState.collection was captured BEFORE we fixed it during registration.
    // If the current analyzify.collection has products, DON'T overwrite it.
    // If neither has products, restore the saved one for other properties.
    const currentCollection = analyzify.collection;
    const hasCurrentProducts =
      currentCollection && Array.isArray(currentCollection.products) && currentCollection.products.length > 0;
    const hasSavedProducts =
      savedState.collection &&
      Array.isArray(savedState.collection.products) &&
      savedState.collection.products.length > 0;

    if (hasSavedProducts && !hasCurrentProducts) {
      // Saved state has products, current doesn't - use saved
      analyzify.collection = savedState.collection;
    } else if (hasCurrentProducts) {
      // Current has products - KEEP IT, don't overwrite
      // But merge any other properties from saved state
      if (savedState.collection) {
        analyzify.collection = {
          ...savedState.collection,
          ...currentCollection,
          products: currentCollection.products, // Ensure products are kept
        };
      }
    } else if (savedState.collection) {
      // Neither has products, restore saved state (at least has id/title/handle)
      analyzify.collection = savedState.collection;
    }
    // Else: keep current collection as-is

    // CRITICAL: Sync window.collection with analyzify.collection after restore
    // CDN scripts may access either one, they must be consistent
    if (analyzify.collection) {
      win.collection = analyzify.collection;
    }

    this.analyzify.detectedCart = savedState.detectedCart;
    analyzify.getSearchObj = savedState.getSearchObj;

    // Restore init flags (set to true since initial init already happened)
    analyzify.ga4Initiated = savedState.ga4Initiated;
    analyzify.gtmInitiated = savedState.gtmInitiated;

    // Note: We don't restore ga4InitializeAndTrackPage and gtmInitializeAndTrackPage
    // because CDN overwrites them anyway during each init call.
    // The last defined version is from 'search' page type, which is fine.

    this.analyzify.log('All tracking APIs restored after function registration', 'platform-reinit');
  }

  /**
   * Log which CDN functions are now available
   */
  private logRegisteredFunctions(): void {
    const functions = [
      // GA4
      'gaViewItem',
      'gaViewItemList',
      'gaViewCart',
      'gaSearch',
      'gaVariantChange',
      'gaSelectItem',
      'gaAddToCart',
      'gaRemoveFromCart',
      'gaBeginCheckout',
      // GTM
      'gtmViewItem',
      'gtmViewItemList',
      'gtmViewCart',
      'gtmSearch',
      'gtmVariantChange',
      'gtmSelectItem',
      'gtmAddToCart',
      'gtmRemoveFromCart',
      'gtmBeginCheckout',
    ];

    const available = functions.filter(
      (fn) => typeof (this.analyzify as any)[fn] === 'function'
    );
    const missing = functions.filter(
      (fn) => typeof (this.analyzify as any)[fn] !== 'function'
    );

    this.analyzify.log(
      `CDN functions available: ${available.length}/${functions.length}`,
      'platform-reinit'
    );

    if (missing.length > 0) {
      this.analyzify.log(`Missing functions: ${missing.join(', ')}`, 'platform-reinit');
    }
  }

  /**
   * Check if all functions are registered
   */
  get isRegistered(): boolean {
    return this.functionsRegistered;
  }
}
